import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, User as UserIcon, Wrench, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import GoogleButton, { OrDivider } from "../components/GoogleButton";
import { invitesApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import { useNavigationHistory } from "../contexts/NavigationHistoryContext";

const ROLE_BADGE = {
  admin: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  dispatcher: "bg-blue-50 text-blue-700 border-blue-200",
  technician: "bg-amber-50 text-amber-700 border-amber-200",
  office_personnel: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const { goBack } = useNavigationHistory();

  const [preview, setPreview] = useState(null); // null=loading, object=ok, false=error
  const [previewError, setPreviewError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setPreview(false);
      setPreviewError("Missing invite token. Please use the link from your invitation email.");
      return;
    }
    invitesApi
      .preview(token)
      .then((p) => {
        setPreview(p);
        setName(p.name || "");
      })
      .catch((err) => {
        setPreview(false);
        setPreviewError(formatApiErrorDetail(err?.response?.data?.detail) || "Invitation not found or expired");
      });
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await invitesApi.accept(token, { name: name.trim() || undefined, password });
      await refreshMe();
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left visual */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Wrench className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-white font-bold text-[18px] tracking-tight">ServicePro</div>
              <div className="text-slate-400 text-[11px] uppercase tracking-wider">Field Ops</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-white text-4xl font-bold leading-tight mb-3">
            Welcome aboard.<br />Let's set you up.
          </h1>
          <p className="text-slate-300 text-[14px] leading-relaxed">
            One quick step and you'll be inside the dispatcher console — managing jobs, customers, and your team.
          </p>
        </div>
        <div className="relative z-10 text-slate-500 text-[11px]">© 2026 ServicePro Field Ops</div>
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }} />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {preview === null && (
            <div data-testid="invite-loading" className="flex items-center gap-2.5 text-slate-500">
              <div className="h-5 w-5 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
              <span className="text-[13.5px]">Validating your invitation…</span>
            </div>
          )}
          {preview === false && (
            <div data-testid="invite-error" className="space-y-4">
              <div className="h-12 w-12 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
              <h2 className="text-[22px] font-bold text-slate-900">Invitation unavailable</h2>
              <p className="text-[13.5px] text-slate-600">{previewError}</p>
              <button type="button" onClick={() => goBack("/login")} className="inline-flex items-center gap-1.5 text-[13px] text-blue-600 hover:text-blue-700 font-semibold">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
              </button>
            </div>
          )}
          {preview && (
            <>
              <div data-testid="invite-preview-card" className="mb-6 p-4 rounded-xl border border-blue-100 bg-blue-50/50">
                <div className="flex items-center gap-2.5 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  <div className="text-[14px] font-semibold text-slate-900">You've been invited</div>
                </div>
                <div className="text-[12.5px] text-slate-600 space-y-1">
                  <div>
                    <strong>{preview.invited_by_name}</strong> invited <strong>{preview.email}</strong>
                    {preview.company_name ? <> to join <strong>{preview.company_name}</strong></> : null}
                  </div>
                  <div className="flex items-center gap-1.5">
                    Role:
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-semibold border ${ROLE_BADGE[preview.role] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {preview.role}
                    </span>
                  </div>
                </div>
              </div>

              <h2 className="text-[24px] font-bold text-slate-900 mb-1.5">Set up your account</h2>
              <p className="text-[13.5px] text-slate-500 mb-5">
                Continue with Google for a one-click sign-up, or pick a password below.
              </p>

              <div className="space-y-3.5 mb-5">
                <GoogleButton
                  label={`Continue with Google as ${preview.email}`}
                  inviteToken={token}
                  testid="accept-invite-google-button"
                />
                <OrDivider />
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="invite-accept-form">
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Display name</label>
                  <div className="relative">
                    <UserIcon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      data-testid="invite-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="password"
                      required
                      minLength={6}
                      autoFocus
                      data-testid="invite-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                    />
                  </div>
                </div>
                {error && (
                  <div data-testid="invite-error-banner" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="invite-submit-button"
                  className="w-full h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[14px] font-semibold"
                >
                  {submitting ? "Creating account…" : "Create account & sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
